import jwt_decode from 'jwt-decode';
import {
  AnonymousJwtPayload,
  AuthBloomaysContext,
  Auth0Context,
  Auth0User,
  ILogger,
  LogoutOptions,
} from '@bloomays-lib/types.shared';
import clone from 'lodash/fp/clone';
import { stripLegacyAnonymousRole } from '../../core/roles.utils';

export const GetAnonymousUser = (
  token: string | undefined | null,
  logout: () => void,
  loginWithRedirect: () => void,
  logger?: ILogger,
): AuthBloomaysContext => {
  if (token && token.includes('anonymous:')) {
    logger?.debug('GetAnonymousUser 👀', { token });
    try {
      const decodedToken: AnonymousJwtPayload = jwt_decode(token);

      // Vérifier l'expiration du token
      if (decodedToken.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (decodedToken.exp < currentTime) {
          logger?.debug('GetAnonymousUser ❌ Token expired', {
            exp: decodedToken.exp,
            currentTime,
          });
          // Ne pas stocker le token expiré dans localStorage
          localStorage.removeItem('token');
          // Retourner un contexte non authentifié pour forcer une nouvelle authentification
          return {
            isAnonymous: true,
            user: undefined,
            isAuthenticated: false,
            isLoading: false,
            error: new Error('Token expired'),
            logout,
            loginWithRedirect,
            getAccessTokenSilently: () => Promise.resolve(undefined),
          };
        }
      }

      const context: AuthBloomaysContext = {
        isAnonymous: true,
        user: decodedToken.user,
        isAuthenticated: true,
        isLoading: false,
        error: undefined,
        logout: logout,
        loginWithRedirect: loginWithRedirect,
        getAccessTokenSilently: () => Promise.resolve(token),
      };
      if (context.user) {
        // Le marqueur `sub` (racine du JWT) identifie un visiteur magic-link ;
        // on le propage sur le user pour que les gardes front (ProtectedRoute, Bucket) puissent le lire.
        context.user.sub = decodedToken.sub;
        context.user.email_verified = true;
      }
      delete decodedToken.user;
      logger?.debug('GetAnonymousUser ✅', { token });
      return context;
    } catch (error: unknown) {
      const context: AuthBloomaysContext = {
        isAnonymous: true,
        user: undefined,
        isAuthenticated: false,
        isLoading: false,
        error: error as Error,
        logout,
        loginWithRedirect,
        getAccessTokenSilently: () => Promise.resolve(undefined),
      };
      logger?.debug('GetAnonymousUser ❌', context);
      return context;
    }
  }
  return {
    isAnonymous: true,
    error: new Error('No token found'),
    user: undefined,
    isAuthenticated: false,
    isLoading: false,
    logout,
    loginWithRedirect,
    getAccessTokenSilently: () => Promise.resolve(undefined),
  };
};

export const GetAuth0User = (
  { user, isLoading, isAuthenticated, error, getAccessTokenSilently, loginWithRedirect, logout }: Auth0Context,
  logger?: ILogger,
): AuthBloomaysContext => {
  let clonedUser: Auth0User | undefined;
  if (user) {
    clonedUser = clone(user);
    logger?.debug('GetAuth0User 👀', clonedUser);
    // Patch rétro-compatible : on retire un éventuel rôle « Anonymous » legacy du claim.
    clonedUser.roles = stripLegacyAnonymousRole(user?.['https://www.bloomays.com/roles']);
    clonedUser.displayName = user.name || user.displayName;
    delete clonedUser.name;
  }
  const context = {
    isAnonymous: false,
    user: clonedUser,
    isLoading,
    isAuthenticated,
    error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  };
  logger?.debug('GetAuth0User ✅', context);
  return context;
};

export const AuthFactory = (
  args: { auth0context: Auth0Context; location: string },
  logger?: ILogger,
): AuthBloomaysContext => {
  const query = new URL(args.location).searchParams;
  const qToken = query.get('token');
  const logoutHandler = (options?: LogoutOptions) => {
    localStorage.removeItem('token');
    //legacy migration, could be remove in few month if no issue
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('originalPathAfterAuth');
    return args.auth0context.logout(options);
  };
  if (qToken) {
    logger?.debug('AuthFactory 👀', { qToken });
    localStorage.setItem('token', qToken);
    // Stocker l'URL d'origine sans le token pour la redirection après authentification
    const urlWithoutToken = new URL(args.location);
    urlWithoutToken.searchParams.delete('token');
    const originalPath = urlWithoutToken.pathname + urlWithoutToken.search;
    sessionStorage.setItem('originalPathAfterAuth', originalPath);
  }
  // sessionStorage is used for legacy migration, could be remove in few month if no issue
  const token = qToken || localStorage.getItem('token') || sessionStorage.getItem('token') || undefined;
  const { user, isLoading, isAuthenticated, error, logout, loginWithRedirect, getAccessTokenSilently, isAnonymous } =
    GetAnonymousUser(token, logoutHandler, args.auth0context.loginWithRedirect, logger);
  if (!isAuthenticated) {
    return GetAuth0User({ ...args.auth0context, logout: logoutHandler }, logger);
  }
  return { user, isLoading, isAuthenticated, error, logout, loginWithRedirect, getAccessTokenSilently, isAnonymous };
};

/**
 * Custom error class for Auth0 token expiration/refresh failures
 */
export class Auth0TokenExpiredError extends Error {
  public readonly isAuth0TokenExpired = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message = 'Auth0 token has expired and needs refresh', public readonly originalError?: any) {
    super(message);
    this.name = 'Auth0TokenExpiredError';

    // Maintain proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, Auth0TokenExpiredError);
    }
  }
}

/**
 * Determines if an error is related to Auth0 token refresh failures
 * @param error - The error to inspect (can be any type)
 * @returns true if the error is related to Auth0 token refresh issues
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAuth0TokenRefreshError = (error: any): boolean => {
  if (!error) {
    return false;
  }

  // Check if it's our custom error
  if (error instanceof Auth0TokenExpiredError || error.isAuth0TokenExpired) {
    return true;
  }

  // Direct error properties
  if (error.error === 'invalid_grant' || error.error === 'missing_transaction') {
    return true;
  }

  // Error message checks
  const message = String(error.message || '');
  if (
    message.includes('invalid_grant') ||
    message.includes('missing_transaction') ||
    message.includes('Login required') ||
    message.includes('Unknown or invalid refresh token')
  ) {
    return true;
  }

  // Check for Auth0 specific error descriptions
  if (error.error_description && String(error.error_description).includes('Unknown or invalid refresh token')) {
    return true;
  }

  // Check error cause chain
  const cause = error.cause;
  if (cause) {
    const isCauseAuth0TokenRefreshError = isAuth0TokenRefreshError(cause);
    if (isCauseAuth0TokenRefreshError) {
      return true;
    }
  }

  return false;
};

/**
 * Determines if an error is an Auth0 session expired error from userStore
 * @param error - The error to inspect
 * @returns true if the error indicates Auth0 session expiration from GraphQL queries
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const isAuth0SessionExpiredError = (error: any): boolean => {
  if (!error) return false;

  return (
    (error as any).isAuth0SessionExpired === true ||
    String(error.message || '').includes('Auth0 session expired during user fetch')
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */
